/**
 * Working out the other environment's address.
 *
 * A staging environment's web address is its parent's with `-staging` on the
 * end, and the pages of both live at `/p/<that address's first label>`. Three
 * places need to get from one to the other (the rail's environment switch, the
 * red banner's way out, and the settings section's link), so the derivation
 * lives here once rather than three times.
 *
 * The suffix is a platform rule, not a guess: apps/cloud reserves it, refuses
 * it to everybody else, and derives every sibling's address from the same
 * constant.
 */

import type { Project } from './types';

export const STAGING_SUFFIX = '-staging';

/** The slug of the live app, given either environment of it. */
export function liveSlugOf(project: Project): string {
  return project.environment === 'staging'
    ? project.slug.slice(0, -STAGING_SUFFIX.length)
    : project.slug;
}

/**
 * The slug of this project's staging environment, or null when it has none.
 *
 * Read off the environment's own web address rather than assumed from the
 * parent's slug, because the address is the thing that actually exists: an
 * owner who supplied their own name for it has one that does not end in the
 * suffix at all.
 */
export function stagingSlugOf(project: Project): string | null {
  if (project.environment === 'staging') return project.slug;
  const address = project.environments.staging?.webAddress;
  if (!address) return null;
  try {
    return new URL(address).hostname.split('.')[0] ?? null;
  } catch {
    return null;
  }
}

/** Whether this project is one half of a pair, either half. */
export function hasTwoEnvironments(project: Project): boolean {
  return project.environment === 'staging' || !!project.environments.staging;
}

/**
 * The same screen on the other environment.
 *
 * Keeps whatever comes after `/p/<slug>`, because the reason somebody switches
 * is almost always to see the SAME thing on the other side: the keys, the
 * tables, the deploys. Dropping them onto the overview each time would make
 * comparing two environments a navigation exercise.
 */
export function sameScreenOn(pathname: string, fromSlug: string, toSlug: string): string {
  const prefix = `/p/${encodeURIComponent(fromSlug)}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';
  return `/p/${encodeURIComponent(toSlug)}${rest}`;
}
